import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import api from '../../lib/api';
import logoIcon from '../../assets/logo-icon.png';
import logoWordmark from '../../assets/logo-wordmark.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';
import {
  Menu,
  User,
  LogOut,
  LayoutDashboard,
  Heart,
  MessageSquare,
  Plus,
  Settings,
  Shield,
  Search,
  ChevronDown,
} from 'lucide-react';

export const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);

  useEffect(() => {
    api.get('/categories').then(res => {
      setDynamicCategories(res.data.slice(0, 4));
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    if (user.role === 'admin') return '/admin';
    return '/dashboard';
  };

  const navLinks = [
    { href: '/browse', label: 'Explorar' },
    ...dynamicCategories.map(c => ({ href: `/browse?category=${c.id}`, label: c.name })),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group" data-testid="logo-link">
            <img
              src={logoIcon}
              alt=""
              aria-hidden="true"
              className="h-14 sm:h-16 w-auto group-hover:opacity-90 transition-opacity duration-200"
            />
            <img
              src={logoWordmark}
              alt="VehiMax - Todos los Vehículos. Una Plataforma"
              className="h-8 sm:h-10 w-auto rounded-sm group-hover:opacity-90 transition-opacity duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-accent'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/browse" data-testid="search-btn">
              <Button variant="ghost" size="icon" className="rounded-sm">
                <Search className="h-5 w-5" />
              </Button>
            </Link>

            {isAuthenticated ? (
              <>
                {(user?.role === 'seller' || user?.role === 'dealer' || user?.role === 'admin') && (
                  <Link to="/listings/create" data-testid="create-listing-btn">
                    <Button className="btn-accent h-10 px-4 text-sm">
                      <Plus className="h-4 w-4 mr-2" />Publicar</Button>
                  </Link>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-10 px-3 rounded-sm" data-testid="user-menu-btn">
                      {user?.profile_photo ? (
                        <img src={user.profile_photo} alt="" className="h-6 w-6 rounded-full object-cover mr-2" />
                      ) : (
                        <User className="h-4 w-4 mr-2" />
                      )}
                      <span className="max-w-[100px] truncate">{user?.name}</span>
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to={getDashboardLink()} className="flex items-center cursor-pointer" data-testid="dashboard-link">
                        <LayoutDashboard className="h-4 w-4 mr-2" />Panel</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/saved" className="flex items-center cursor-pointer" data-testid="saved-link">
                        <Heart className="h-4 w-4 mr-2" />Guardados</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center cursor-pointer" data-testid="messages-link">
                        <MessageSquare className="h-4 w-4 mr-2" />Mensajes</Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center cursor-pointer" data-testid="admin-link">
                            <Shield className="h-4 w-4 mr-2" />Panel de administrador</Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center cursor-pointer" data-testid="settings-link">
                        <Settings className="h-4 w-4 mr-2" />
                        Configuración
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                      <LogOut className="h-4 w-4 mr-2" />Cerrar sesión</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="h-10 px-4 rounded-sm uppercase font-semibold text-sm">Iniciar sesión</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="btn-industrial h-10 px-4 text-sm">Registrarse</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <div className="flex flex-col gap-6 mt-6 pb-6">
                {isAuthenticated && (user?.role === 'seller' || user?.role === 'dealer' || user?.role === 'admin') && (
                  <Link
                    to="/listings/create"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 flex items-center justify-center gap-3 bg-accent text-white rounded-sm font-semibold uppercase"
                    data-testid="mobile-publish-btn"
                  >
                    <Plus className="h-5 w-5" />Publicar</Link>
                )}

                <div className="flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 text-lg font-medium uppercase tracking-wide hover:bg-secondary rounded-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                <div className="border-t border-border pt-4">
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-2">
                      <Link
                        to={getDashboardLink()}
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 flex items-center gap-3 hover:bg-secondary rounded-sm"
                        data-testid="mobile-dashboard-link"
                      >
                        <LayoutDashboard className="h-5 w-5" />Panel</Link>
                      <Link
                        to="/saved"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 flex items-center gap-3 hover:bg-secondary rounded-sm"
                        data-testid="mobile-saved-link"
                      >
                        <Heart className="h-5 w-5" />Guardados</Link>
                      <Link
                        to="/messages"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 flex items-center gap-3 hover:bg-secondary rounded-sm"
                        data-testid="mobile-messages-link"
                      >
                        <MessageSquare className="h-5 w-5" />Mensajes</Link>
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 flex items-center gap-3 hover:bg-secondary rounded-sm"
                        data-testid="mobile-settings-link"
                      >
                        <Settings className="h-5 w-5" />Configuración</Link>
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-3 flex items-center gap-3 hover:bg-secondary rounded-sm"
                          data-testid="mobile-admin-link"
                        >
                          <Shield className="h-5 w-5" />Panel de administrador</Link>
                      )}
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="px-4 py-3 flex items-center gap-3 text-red-600 hover:bg-red-50 rounded-sm mt-2"
                        data-testid="mobile-logout-btn"
                      >
                        <LogOut className="h-5 w-5" />Cerrar sesión</button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 text-center font-semibold uppercase hover:bg-secondary rounded-sm"
                      >Iniciar sesión</Link>
                      <Link
                        to="/register"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 text-center font-semibold uppercase bg-primary text-white rounded-sm"
                      >Registrarse</Link>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
